/**
 * Part 10 — Integration Breadth service.
 *
 * Wraps the /integrations-ext endpoints:
 *   - A10.5 task-ref prefix
 *   - A10.2 per-project mailboxes
 *   - A10.3 calendar connections + ICS feed
 *   - A10.4 storage-provider connections
 *   - A10.6 notification channels (SMS / push / webhook)
 */
import { apiClient } from './api'

export interface Mailbox {
  id: number
  project_id: number
  address_token: string
  default_action: 'comment' | 'task'
  is_active: boolean
  address: string | null
  created_at: string | null
}

export interface CalendarConnection {
  id: number
  provider: 'google' | 'outlook'
  calendar_id: string | null
  sync_direction: 'push' | 'pull' | 'both'
  is_active: boolean
  last_sync_at: string | null
  created_at: string | null
}

export interface StorageConnection {
  id: number
  provider: 'gdrive' | 'onedrive' | 'dropbox'
  root_folder_id: string | null
  is_default: boolean
  is_active: boolean
  created_at: string | null
}

export interface ChannelConfig {
  id: number
  channel: 'sms' | 'push' | 'webhook'
  user_id: number | null
  is_active: boolean
  verified_at: string | null
  config: Record<string, unknown> | null
  created_at: string | null
}

export interface ChannelTestResult {
  channel: string
  ok: boolean
  skipped: boolean
  detail: string
}

const base = '/api/v1/integrations-ext'

export const integrationsBreadthService = {
  // A10.5 ref prefix
  getRefPrefix: () => apiClient.get<{ prefix: string }>(`${base}/ref-prefix`).then(r => r.data),
  setRefPrefix: (prefix: string) =>
    apiClient.put<{ prefix: string }>(`${base}/ref-prefix`, { prefix }).then(r => r.data),

  // A10.2 mailboxes
  listMailboxes: (project_id?: number) =>
    apiClient.get<Mailbox[]>(`${base}/mailboxes`, { params: { project_id } }).then(r => r.data),
  createMailbox: (project_id: number, default_action: 'comment' | 'task' = 'comment') =>
    apiClient.post<Mailbox>(`${base}/mailboxes`, { project_id, default_action }).then(r => r.data),
  deleteMailbox: (id: number) =>
    apiClient.delete(`${base}/mailboxes/${id}`).then(() => undefined),

  // A10.3 calendar
  listCalendarConnections: () =>
    apiClient.get<CalendarConnection[]>(`${base}/calendar/connections`).then(r => r.data),
  deleteCalendarConnection: (id: number) =>
    apiClient.delete(`${base}/calendar/connections/${id}`).then(() => undefined),
  getCalendarFeedUrl: () =>
    apiClient.get<{ url: string }>(`${base}/calendar/feed-url`).then(r => r.data),

  // A10.4 storage
  listStorageConnections: () =>
    apiClient.get<StorageConnection[]>(`${base}/storage/connections`).then(r => r.data),
  deleteStorageConnection: (id: number) =>
    apiClient.delete(`${base}/storage/connections/${id}`).then(() => undefined),

  // A10.6 channels
  listChannels: () =>
    apiClient.get<ChannelConfig[]>(`${base}/channels`).then(r => r.data),
  createChannel: (body: { channel: 'sms' | 'push' | 'webhook'; target: string; secret?: string; config?: Record<string, unknown> }) =>
    apiClient.post<ChannelConfig>(`${base}/channels`, body).then(r => r.data),
  deleteChannel: (id: number) =>
    apiClient.delete(`${base}/channels/${id}`).then(() => undefined),
  testChannel: (id: number) =>
    apiClient.post<ChannelTestResult>(`${base}/channels/${id}/test`).then(r => r.data),
}
